import { useState, useEffect } from 'react';
import { reminderAPI, appointmentAPI } from '../../lib/api';
import { 
  Bell, 
  Envelope, 
  DeviceMobileSpeaker,
  PaperPlaneTilt,
  CheckCircle,
  XCircle,
  Clock,
  Calendar,
  User,
  MagnifyingGlass,
  Gear,
  Warning,
  CaretDown,
  CaretUp,
  Phone
} from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function AdminRemindersPage() {
  const [configStatus, setConfigStatus] = useState(null);
  const [pendingReminders, setPendingReminders] = useState([]);
  const [reminderLogs, setReminderLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingBatch, setSendingBatch] = useState(false);
  const [sendingIndividual, setSendingIndividual] = useState(null);
  const [showLogs, setShowLogs] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [configRes, pendingRes, logsRes] = await Promise.all([
        reminderAPI.getConfigStatus().catch(() => ({ data: {} })),
        reminderAPI.getPending().catch(() => ({ data: { pending_reminders: [] } })),
        reminderAPI.getLogs({ limit: 50 }).catch(() => ({ data: { logs: [] } }))
      ]);
      
      setConfigStatus(configRes.data);
      setPendingReminders(pendingRes.data.pending_reminders || []);
      setReminderLogs(logsRes.data.logs || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load reminder data');
    } finally {
      setLoading(false);
    }
  };

  const handleSendBatchReminders = async () => {
    setSendingBatch(true);
    try {
      const response = await reminderAPI.sendBatch();
      const results = response.data;
      toast.success(`Sent ${results.email_sent} emails and ${results.sms_sent} SMS messages`);
      loadData(); // Refresh
    } catch (error) {
      toast.error('Failed to send batch reminders');
    } finally {
      setSendingBatch(false);
    }
  };

  const handleSendIndividual = async (appointmentId, sendEmail = true, sendSms = true) => {
    setSendingIndividual(appointmentId);
    try {
      const response = await reminderAPI.sendReminder({
        appointment_id: appointmentId,
        send_email: sendEmail,
        send_sms: sendSms
      });
      
      const results = response.data;
      if (results.email_sent || results.sms_sent) {
        toast.success('Reminder sent successfully');
      } else {
        toast.error(`Failed: ${results.email_error || results.sms_error || 'Unknown error'}`);
      }
      loadData();
    } catch (error) {
      toast.error('Failed to send reminder');
    } finally {
      setSendingIndividual(null);
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
      return 'N/A';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return 'N/A';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Appointment Reminders</h1>
          <p className="text-slate-500">Send email and SMS reminders to patients</p>
        </div>
        <button
          onClick={handleSendBatchReminders}
          disabled={sendingBatch || pendingReminders.length === 0}
          className="btn-teal flex items-center gap-2 disabled:opacity-50"
        >
          {sendingBatch ? (
            <>
              <div className="spinner w-4 h-4 border-white" />
              Sending...
            </>
          ) : (
            <>
              <PaperPlaneTilt size={18} />
              Send All Pending ({pendingReminders.length})
            </>
          )}
        </button>
      </div>

      {/* Configuration Status */}
      <div className="card-clinical p-4">
        <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Gear size={18} className="text-slate-500" />
          Service Configuration
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className={`p-3 rounded-lg ${configStatus?.sendgrid_configured ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
            <div className="flex items-center gap-2">
              <Envelope size={20} className={configStatus?.sendgrid_configured ? 'text-green-600' : 'text-red-600'} />
              <span className="font-medium">Email (SendGrid)</span>
            </div>
            <p className={`text-sm mt-1 ${configStatus?.sendgrid_configured ? 'text-green-700' : 'text-red-700'}`}>
              {configStatus?.sendgrid_configured ? 'Configured' : 'Not Configured'}
            </p>
          </div>
          
          <div className={`p-3 rounded-lg ${configStatus?.sms_ready ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
            <div className="flex items-center gap-2">
              <DeviceMobileSpeaker size={20} className={configStatus?.sms_ready ? 'text-green-600' : 'text-red-600'} />
              <span className="font-medium">SMS (Twilio)</span>
            </div>
            <p className={`text-sm mt-1 ${configStatus?.sms_ready ? 'text-green-700' : 'text-red-700'}`}>
              {configStatus?.sms_ready ? 'Configured' : 'Not Configured'}
            </p>
          </div>
          
          <div className={`p-3 rounded-lg ${configStatus?.twilio_phone_configured ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
            <div className="flex items-center gap-2">
              <Phone size={20} className={configStatus?.twilio_phone_configured ? 'text-green-600' : 'text-amber-600'} />
              <span className="font-medium">Twilio Phone</span>
            </div>
            <p className={`text-sm mt-1 ${configStatus?.twilio_phone_configured ? 'text-green-700' : 'text-amber-700'}`}>
              {configStatus?.twilio_phone_configured ? 'Configured' : 'Not Set'}
            </p>
          </div>
          
          <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
            <div className="flex items-center gap-2">
              <Envelope size={20} className="text-slate-600" />
              <span className="font-medium">Sender Email</span>
            </div>
            <p className="text-sm mt-1 text-slate-600 truncate">
              {configStatus?.sender_email || 'Not configured'}
            </p>
          </div>
        </div>
        
        {(!configStatus?.sendgrid_configured || !configStatus?.sms_ready) && (
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start gap-2">
              <Warning size={18} className="text-amber-600 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">Configuration Required</p>
                <p className="text-sm text-amber-700">
                  Set the following environment variables to enable reminders:
                  {!configStatus?.sendgrid_configured && <span className="block">• SENDGRID_API_KEY, SENDER_EMAIL (for email)</span>}
                  {!configStatus?.sms_ready && <span className="block">• TWILIO_ACCOUNT_SID, TWILIO_AUTH_TOKEN, TWILIO_PHONE_NUMBER (for SMS)</span>}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card-clinical p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Clock size={20} className="text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{pendingReminders.length}</p>
              <p className="text-sm text-slate-500">Pending</p>
            </div>
          </div>
        </div>
        <div className="card-clinical p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle size={20} className="text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">
                {reminderLogs.filter(l => l.status === 'sent').length}
              </p>
              <p className="text-sm text-slate-500">Sent (Recent)</p>
            </div>
          </div>
        </div>
        <div className="card-clinical p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <XCircle size={20} className="text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">
                {reminderLogs.filter(l => l.status === 'failed').length}
              </p>
              <p className="text-sm text-slate-500">Failed (Recent)</p>
            </div>
          </div>
        </div>
        <div className="card-clinical p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Bell size={20} className="text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{reminderLogs.length}</p>
              <p className="text-sm text-slate-500">Total Logged</p>
            </div>
          </div>
        </div>
      </div>

      {/* Pending Reminders */}
      <div className="card-clinical overflow-hidden">
        <div className="p-4 border-b border-slate-200 bg-amber-50">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2">
            <Clock size={18} className="text-amber-600" />
            Pending Reminders (24 Hour Window)
          </h3>
        </div>

        {pendingReminders.length === 0 ? (
          <div className="p-8 text-center">
            <Bell size={48} className="text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No pending reminders at this time</p>
            <p className="text-sm text-slate-400 mt-1">Appointments within 24 hours will appear here</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {pendingReminders.map((apt) => (
              <div key={apt.id} className="p-4 hover:bg-slate-50" data-testid={`pending-reminder-${apt.id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="text-center min-w-[80px]">
                      <p className="text-sm text-slate-500">{formatDate(apt.scheduled_time)}</p>
                      <p className="text-lg font-semibold text-teal-600">{formatTime(apt.scheduled_time)}</p>
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <User size={16} className="text-slate-400" />
                        <p className="font-medium text-slate-900">{apt.patient_name}</p>
                      </div>
                      <p className="text-sm text-slate-500">
                        {apt.appointment_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleSendIndividual(apt.id, true, false)}
                      disabled={sendingIndividual === apt.id || !configStatus?.sendgrid_configured}
                      className="px-3 py-1.5 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg text-sm flex items-center gap-1 disabled:opacity-50"
                      title="Send email only"
                    >
                      <Envelope size={14} />
                      Email
                    </button>
                    <button
                      onClick={() => handleSendIndividual(apt.id, false, true)}
                      disabled={sendingIndividual === apt.id || !configStatus?.sms_ready}
                      className="px-3 py-1.5 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg text-sm flex items-center gap-1 disabled:opacity-50"
                      title="Send SMS only"
                    >
                      <DeviceMobileSpeaker size={14} />
                      SMS
                    </button>
                    <button
                      onClick={() => handleSendIndividual(apt.id, true, true)}
                      disabled={sendingIndividual === apt.id}
                      className="px-3 py-1.5 bg-teal-100 hover:bg-teal-200 text-teal-700 rounded-lg text-sm flex items-center gap-1 disabled:opacity-50"
                      title="Send both"
                    >
                      {sendingIndividual === apt.id ? (
                        <div className="spinner w-3 h-3" />
                      ) : (
                        <PaperPlaneTilt size={14} />
                      )}
                      Both
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent Logs */}
      <div className="card-clinical overflow-hidden">
        <div 
          className="p-4 border-b border-slate-200 bg-slate-50 cursor-pointer flex items-center justify-between"
          onClick={() => setShowLogs(!showLogs)}
        >
          <h3 className="font-semibold text-slate-900 flex items-center gap-2">
            <Bell size={18} className="text-slate-600" />
            Recent Reminder Logs ({reminderLogs.length})
          </h3>
          {showLogs ? <CaretUp size={20} /> : <CaretDown size={20} />}
        </div>

        {showLogs && (
          reminderLogs.length === 0 ? (
            <div className="p-8 text-center">
              <Bell size={48} className="text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No reminder logs yet</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100 max-h-96 overflow-y-auto">
              {reminderLogs.map((log) => (
                <div key={log.id} className="p-3 hover:bg-slate-50 text-sm">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {log.reminder_type === 'email' ? (
                        <Envelope size={16} className="text-blue-500" />
                      ) : (
                        <DeviceMobileSpeaker size={16} className="text-green-500" />
                      )}
                      <span className="text-slate-600">{log.reminder_type.toUpperCase()}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs ${
                        log.status === 'sent' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {log.status}
                      </span>
                    </div>
                    <span className="text-slate-400 text-xs">
                      {formatDate(log.created_at)} {formatTime(log.created_at)}
                    </span>
                  </div>
                  {log.error_message && (
                    <p className="text-red-600 text-xs mt-1 ml-7">{log.error_message}</p>
                  )}
                </div>
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
}
